/* storybook-check-ignore */
import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import LogInConnector, { AccountProps } from '../shared/AccountConnector';
import Box from '@opendoor/bricks/core/Box';
import { Flex, Link, Image, Text } from '@opendoor/bricks/core';
import HeaderMenu from './layout/HeaderMenu';
import HamburgerMenuIcon from './icons/HamburgerMenuIcon';
import QuestionMarkIcon from './icons/QuestionMark';
import { AnimatePresence, motion } from 'framer-motion';

const Wrapper = styled.header`
  background: #ffffff;
  top: 0;
  left: 0;
  width: 100%;
  border-bottom: 1px solid var(--colors-neutrals40);
  min-height: 4.2rem;
  padding: 20px 1.5rem 15px 1.5rem;
  display: flex;
`;

interface Props {
  logoToGallery?: boolean;
  tooltipText?: string;
  tooltip?: boolean;
  onTooltipDismiss?: () => void;
}

const TouringHeader: React.FC<Props & AccountProps> = ({
  logoToGallery,
  maybeFetchSessionInfo,
  maybeFetchSessionLeads,
  tooltip,
  tooltipText,
  onTooltipDismiss,
  ...props
}) => {
  const [expanded, setExpanded] = useState(false);
  const questionMarkRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    maybeFetchSessionInfo();
    maybeFetchSessionLeads();
  }, []);

  const handleExpandedState = (state: boolean) => () => setExpanded(state);

  return (
    <Flex flexDirection="column" position="sticky" width="100%" zIndex={2}>
      <Wrapper>
        <Box as="a" href={logoToGallery ? '/exclusives/gallery' : '/exclusives'} rel="home">
          <Image
            height="18px"
            maxWidth={['175px', '100%']}
            alt="Opendoor Exclusives logo"
            src="https://imgdrop.imgix.net/df3f4c2922ba4c4dbcc2f8142f9b3dbe.svg"
          />
        </Box>

        <Flex justifyContent="space-between" ml="auto" alignItems="center">
          <Link
            fontSize="s1"
            color="neutrals100"
            href="tel:(512) 706-9938"
            display={['none', 'none', 'flex']}
            aria-label="Call us (512) 706-9938"
            analyticsName="cosmos-exclusives-header-contact-phone"
          >
            Call:
            <Text ml="0.3rem" fontWeight="semibold" color="inherit">
              512-706-9938
            </Text>
          </Link>

          <Link
            id="faq-icon"
            ml="2.5rem"
            height={20}
            fontSize="s1"
            aria-label="FAQ"
            color="neutrals100"
            fontWeight="semibold"
            href="/exclusives/faq"
            ref={questionMarkRef as any}
            analyticsName="cosmos-exclusives-header-FAQs"
          >
            <QuestionMarkIcon />
          </Link>

          <Box
            pt="2px"
            ml="1rem"
            width={20}
            height={20}
            cursor="pointer"
            display="inline"
            onClick={handleExpandedState(true)}
          >
            <HamburgerMenuIcon />
          </Box>

          <HeaderMenu isExpanded={expanded} onClose={handleExpandedState(false)} {...props} />
        </Flex>
      </Wrapper>

      <AnimatePresence>
        {tooltip && (
          <TooltipContainer
            exit={{ opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            initial={{ y: -5, opacity: 0 }}
            left={questionMarkRef.current?.getBoundingClientRect().right}
          >
            <Text
              as="div"
              color="neutrals0"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              fontSize={['s00', 's00', 's0', 's0']}
            >
              {tooltipText || 'These homes are only available on Opendoor Exclusives'}
              <Box p="0.5rem" cursor="pointer" onClick={onTooltipDismiss}>
                <CheckmarkIcon />
              </Box>
            </Text>
          </TooltipContainer>
        )}
      </AnimatePresence>
    </Flex>
  );
};

const TooltipContainer = styled(motion.div)<{ left?: number }>`
  background-color: var(--colors-brand50);
  padding: 5px 18px;
  position: relative;

  &::before {
    content: '';
    margin: auto;
    display: block;
    width: 8px;
    height: 8px;
    position: absolute;
    top: calc(0% - 4px);
    transform: rotate(45deg);
    left: calc(${(props) => props.left}px - 15px);
    background-color: var(--colors-brand50);
  }
`;

const CheckmarkIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="11" viewBox="0 0 14 11" fill="none">
      <path opacity="0.7" d="M1 6.14286L4.6 10L13 1" stroke="var(--colors-neutrals0)" />
    </svg>
  );
};

export default LogInConnector(TouringHeader);
